import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Target, Lightbulb, GitCompare, Ban, Search, X, Check, Loader2 } from 'lucide-react';
import CampaignKeywords from './CampaignKeywords';
import KeywordSuggestions from '../keywords/KeywordSuggestions';
import { campaignService } from '../../services/campaignService';
import { keywordService } from '../../services/keywordService';
import Button from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import { Keyword } from '../../types';

interface KeywordSuggestion {
  keyword: string;
  searchVolume: number;
  difficulty: number;
  cpc: number;
  trend: 'up' | 'down' | 'stable';
  intent: 'informational' | 'transactional' | 'navigational' | 'commercial';
  competition: 'low' | 'medium' | 'high';
  opportunityScore?: number;
}

type SubTab = 'all' | 'suggestions' | 'compare';

interface CampaignKeywordsHubProps {
  campaignId: string;
  campaignStatus?: string;
}

const getKeywordString = (kw: any): string => {
  if (typeof kw === 'string') return kw;
  if (kw && typeof kw === 'object') return kw.value || kw.keyword || kw.text || kw.name || JSON.stringify(kw);
  return String(kw || '');
};

const getDifficultyBarColor = (difficulty: number) => {
  if (difficulty <= 30) return 'bg-green-500';
  if (difficulty <= 60) return 'bg-yellow-500';
  return 'bg-red-500';
};

const CampaignKeywordsHub: React.FC<CampaignKeywordsHubProps> = ({ campaignId, campaignStatus }) => {
  const { t } = useTranslation();
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('all');
  const [keywordType, setKeywordType] = useState<'positive' | 'negative'>('positive');

  // AI Suggestions state
  const [suggestions, setSuggestions] = useState<KeywordSuggestion[]>(() => {
    try {
      const saved = localStorage.getItem(`keyword_suggestions_${campaignId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.filter((s: any) => s.keyword && typeof s.keyword === 'string' && !s.keyword.includes('[object Object]'));
      }
    } catch {}
    return [];
  });
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestionTopic, setSuggestionTopic] = useState(() => {
    try { return localStorage.getItem(`keyword_suggestion_topic_${campaignId}`) || ''; } catch { return ''; }
  });

  // Compare state
  const [campaignKeywords, setCampaignKeywords] = useState<Keyword[]>([]);
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);
  const [compareKeywords, setCompareKeywords] = useState<Keyword[]>([]);
  const [loadingCompare, setLoadingCompare] = useState(false);
  // Separado do loadingCompare: um é pra busca inicial das keywords da campanha,
  // o outro é pra execução do compare em si (depois que o user selecionou)
  const [loadingCampaignKeywords, setLoadingCampaignKeywords] = useState(false);

  // Save suggestions to localStorage
  useEffect(() => {
    try {
      if (suggestions.length > 0) {
        localStorage.setItem(`keyword_suggestions_${campaignId}`, JSON.stringify(suggestions));
      } else {
        localStorage.removeItem(`keyword_suggestions_${campaignId}`);
      }
    } catch {}
  }, [suggestions, campaignId]);

  // Load campaign keywords for compare tab
  useEffect(() => {
    if (activeSubTab === 'compare' && campaignKeywords.length === 0) {
      loadCampaignKeywordsForCompare();
    }
  }, [activeSubTab]);

  const loadCampaignKeywordsForCompare = async () => {
    setLoadingCampaignKeywords(true);
    try {
      const response = await campaignService.getCampaignKeywords(campaignId, 'positive');
      if (response.success && response.data) {
        setCampaignKeywords(response.data as any);
      }
    } catch (error) {
      console.error('Error loading keywords for compare:', error);
    } finally {
      setLoadingCampaignKeywords(false);
    }
  };

  const handleGenerateSuggestions = async (topic: string) => {
    setIsLoadingSuggestions(true);
    setSuggestionTopic(topic);
    try {
      localStorage.setItem(`keyword_suggestion_topic_${campaignId}`, topic);
    } catch {}
    try {
      const { pollJobUntilComplete } = await import('../../services/aiJobService');
      const response = await keywordService.generateAISuggestions(topic, 20, true);

      let result = null;
      if (response.async && response.jobId) {
        toast.loading(t('keywords.generatingInBackground', 'Generating suggestions in background...'), { id: 'keyword-gen' });
        result = await pollJobUntilComplete(response.jobId, undefined, 2000, 120000);
        toast.dismiss('keyword-gen');
      } else if (response.success && response.data) {
        result = response.data;
      }

      if (result && Array.isArray(result)) {
        setSuggestions(result);
        toast.success(t('keywords.suggestionsGenerated', 'Suggestions generated!'));
      } else {
        toast.error(t('keywords.noSuggestionsFound', 'No suggestions found'));
      }
    } catch (error: any) {
      console.error('Error generating suggestions:', error);
      toast.dismiss('keyword-gen');
      toast.error(error.message || t('errors.generic'));
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleAddSuggestedKeywords = async (keywordStrings: string[]) => {
    try {
      await campaignService.addKeywordsToCampaign(campaignId, keywordStrings);
      toast.success(t('keywords.keywordsAdded', { count: keywordStrings.length, defaultValue: `${keywordStrings.length} keywords added` }));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleDeleteSuggestions = (keywordsToDelete: string[]) => {
    setSuggestions(prev => prev.filter(s => !keywordsToDelete.includes(s.keyword)));
  };

  // Compare handlers
  const handleToggleCompare = (keywordId: string) => {
    setSelectedForCompare(prev => {
      const index = prev.indexOf(keywordId);
      if (index > -1) return prev.filter(id => id !== keywordId);
      if (prev.length < 5) return [...prev, keywordId];
      toast.error(t('keywords.maxCompareReached', 'Maximum of 5 keywords to compare'));
      return prev;
    });
  };

  const handleCompare = async () => {
    if (selectedForCompare.length < 2) {
      toast.error(t('keywords.minCompareRequired', 'Select at least 2 keywords'));
      return;
    }
    setLoadingCompare(true);
    try {
      const response = await keywordService.compareKeywords(selectedForCompare);
      if (response.success && response.data) {
        setCompareKeywords(response.data);
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoadingCompare(false);
    }
  };

  const subTabs = [
    { key: 'all' as const, label: t('campaigns.keywordsHub.allKeywords', 'All Keywords'), icon: <Target className="w-4 h-4" /> },
    { key: 'suggestions' as const, label: t('campaigns.keywordsHub.aiSuggestions', 'AI Suggestions'), icon: <Lightbulb className="w-4 h-4" /> },
    { key: 'compare' as const, label: t('campaigns.keywordsHub.compare', 'Compare'), icon: <GitCompare className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-4 min-w-0">
      {/* Sub-tabs — celular: 3 colunas iguais */}
      <div className="grid grid-cols-3 sm:flex items-center gap-1 sm:gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-full sm:w-fit">
        {subTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveSubTab(tab.key)}
            className={`flex items-center justify-center gap-1.5 sm:gap-2 px-1.5 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors min-w-0 text-center leading-tight ${
              activeSubTab === tab.key
                ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <span className="hidden sm:inline-flex">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {activeSubTab === 'all' && (
        <div className="space-y-4">
          {/* Positive/Negative toggle */}
          <div className="grid grid-cols-2 sm:flex items-center gap-1 sm:gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-full sm:w-fit">
            <button
              onClick={() => setKeywordType('positive')}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                keywordType === 'positive'
                  ? 'bg-white dark:bg-gray-700 text-green-600 dark:text-green-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Target className="w-4 h-4" />
              {t('campaigns.keywordsHub.positive', 'Positive')}
            </button>
            <button
              onClick={() => setKeywordType('negative')}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                keywordType === 'negative'
                  ? 'bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Ban className="w-4 h-4" />
              {t('campaigns.keywordsHub.negative', 'Negative')}
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 sm:p-6 min-w-0">
            <CampaignKeywords campaignId={campaignId} type={keywordType} campaignStatus={campaignStatus} />
          </div>

          {/* Help section */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            {keywordType === 'positive' ? (
              <>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-green-500" />
                  {t('campaigns.keywordsHub.matchTypes', 'Match Types')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <p className="font-medium text-green-700 dark:text-green-300 mb-1">
                      {t('campaigns.campaignKeywords.exact', 'Exata')} <code className="text-xs font-normal bg-white dark:bg-gray-800 px-1 rounded">[palavra]</code>
                    </p>
                    <p className="text-green-600 dark:text-green-400 text-xs">
                      {t('campaigns.keywordsHub.exactDescShort', 'Apenas buscas idênticas')}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="font-medium text-blue-700 dark:text-blue-300 mb-1">
                      {t('campaigns.campaignKeywords.phrase', 'Frase')} <code className="text-xs font-normal bg-white dark:bg-gray-800 px-1 rounded">"palavra"</code>
                    </p>
                    <p className="text-blue-600 dark:text-blue-400 text-xs">
                      {t('campaigns.keywordsHub.phraseDescShort', 'Buscas que contenham a frase')}
                    </p>
                  </div>
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <p className="font-medium text-yellow-700 dark:text-yellow-300 mb-1">
                      {t('campaigns.campaignKeywords.broad', 'Ampla')} <code className="text-xs font-normal bg-white dark:bg-gray-800 px-1 rounded">palavra</code>
                    </p>
                    <p className="text-yellow-600 dark:text-yellow-400 text-xs">
                      {t('campaigns.keywordsHub.broadDescShort', 'Buscas relacionadas')}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Ban className="w-4 h-4 text-red-500" />
                  {t('campaigns.keywordsHub.whatAreNegatives', 'What are Negative Keywords?')}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {t('campaigns.keywordsHub.negativesDescription', 'Prevent your ad from showing when these words are in the search.')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {['gratis', 'gratuito', 'free', 'download', 'torrent', 'como fazer', 'tutorial'].map((word) => (
                    <span key={word} className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs rounded-full">{word}</span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeSubTab === 'suggestions' && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 sm:p-6 min-w-0">
          <KeywordSuggestions
            suggestions={suggestions}
            loading={isLoadingSuggestions}
            onAddKeywords={handleAddSuggestedKeywords}
            onGenerateSuggestions={handleGenerateSuggestions}
            onDeleteSuggestions={handleDeleteSuggestions}
            seedKeyword={suggestionTopic}
          />
        </div>
      )}

      {activeSubTab === 'compare' && (
        <div className="space-y-6">
          {/* Keyword Selection */}
          <Card>
            <CardContent>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <div className="flex items-center gap-2 min-w-0">
                  <GitCompare className="w-5 h-5 text-primary-500" />
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    {t('keywords.selectKeywordsToCompare', 'Select keywords to compare')}
                  </h3>
                </div>
                {selectedForCompare.length > 0 && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedForCompare.length}/5 {t('keywords.selected', 'selected')}
                  </span>
                )}
              </div>

              {loadingCampaignKeywords ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-primary-500" />
                  <p className="text-sm">{t('keywords.loadingCampaignKeywords', 'Carregando keywords da campanha...')}</p>
                </div>
              ) : campaignKeywords.length === 0 ? (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>{t('keywords.noKeywordsYet', 'You don\'t have any keywords yet. Add some first.')}</p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {campaignKeywords.map((kw: any) => {
                    const isSelected = selectedForCompare.includes(kw.id);
                    return (
                      <button
                        key={kw.id}
                        onClick={() => handleToggleCompare(kw.id)}
                        disabled={!isSelected && selectedForCompare.length >= 5}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all max-w-full break-words text-left ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                            : selectedForCompare.length >= 5
                            ? 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        {getKeywordString(kw.keyword)}
                        {isSelected && (
                          <span className="ml-2 inline-flex items-center justify-center w-4 h-4 bg-primary-500 text-white rounded-full text-xs">
                            {selectedForCompare.indexOf(kw.id) + 1}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {selectedForCompare.length >= 2 && compareKeywords.length < 2 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button onClick={handleCompare} className="w-full" loading={loadingCompare}>
                    <GitCompare className="w-4 h-4 mr-2" />
                    {t('keywords.compareNow', 'Compare Now')} ({selectedForCompare.length} keywords)
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Loading feedback — visível quando rodando compare antes dos resultados aparecerem */}
          {loadingCompare && compareKeywords.length < 2 && (
            <Card>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-10 h-10 text-primary-500 animate-spin mb-3" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('keywords.comparingInProgress', 'Comparando keywords e buscando métricas do Google Ads...')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comparison Results */}
          {compareKeywords.length >= 2 && (
            <>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('keywords.comparing', 'Comparing')} {compareKeywords.length} keywords
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 break-words">
                    {compareKeywords.map(k => getKeywordString(k.keyword)).join(' vs ')}
                  </p>
                </div>
                <Button variant="secondary" size="sm" onClick={() => { setSelectedForCompare([]); setCompareKeywords([]); }}>
                  <X className="w-4 h-4 mr-1" />
                  {t('keywords.clearComparison', 'Clear')}
                </Button>
              </div>

              <div className={`grid gap-4 ${compareKeywords.length <= 2 ? 'grid-cols-1 md:grid-cols-2' : compareKeywords.length <= 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4 lg:grid-cols-5'}`}>
                {compareKeywords.map((kw: any, index) => {
                  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
                  const color = colors[index % colors.length];
                  // Performance real do keyword_view (últimos 30 dias) — vem no objeto `performance` do backend
                  const perf = kw.performance || {};
                  const maxImpressions = Math.max(...compareKeywords.map((k: any) => k.performance?.impressions || 0));
                  const impressionsPercent = maxImpressions > 0 ? ((perf.impressions || 0) / maxImpressions) * 100 : 0;
                  const maxClicks = Math.max(...compareKeywords.map((k: any) => k.performance?.clicks || 0));
                  const clicksPercent = maxClicks > 0 ? ((perf.clicks || 0) / maxClicks) * 100 : 0;
                  const fmtBRL = (v: any) => `R$ ${(Number(v) || 0).toFixed(2)}`;
                  const fmtInt = (v: any) => (Number(v) || 0).toLocaleString('pt-BR');
                  const fmtPct = (v: any) => `${((Number(v) || 0) * 100).toFixed(2)}%`;

                  return (
                    <Card key={kw.id} className="overflow-hidden">
                      <div className="h-1" style={{ backgroundColor: color }} />
                      <CardContent className="pt-4">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-1 truncate" title={getKeywordString(kw.keyword)}>
                          {getKeywordString(kw.keyword)}
                        </h3>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4 uppercase tracking-wide">
                          {t('keywords.last30days', 'Últimos 30 dias')}
                        </p>

                        {/* Impressões (barra relativa ao max do compare) */}
                        <div className="mb-3">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-gray-500 dark:text-gray-400">{t('campaigns.campaignKeywords.impressions', 'Impressões')}</span>
                            <span className="font-bold text-gray-900 dark:text-white">{fmtInt(perf.impressions)}</span>
                          </div>
                          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${impressionsPercent}%`, backgroundColor: color }} />
                          </div>
                        </div>

                        {/* Cliques (barra relativa) */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-gray-500 dark:text-gray-400">{t('campaigns.campaignKeywords.clicks', 'Cliques')}</span>
                            <span className="font-bold text-gray-900 dark:text-white">{fmtInt(perf.clicks)}</span>
                          </div>
                          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500 opacity-70" style={{ width: `${clicksPercent}%`, backgroundColor: color }} />
                          </div>
                        </div>

                        {/* Métricas numéricas */}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-400">CTR</span>
                            <span className="font-medium text-gray-900 dark:text-white">{fmtPct(perf.ctr)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-400">CPC</span>
                            <span className="font-medium text-gray-900 dark:text-white">{fmtBRL(perf.avgCpc)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('campaigns.campaignKeywords.cost', 'Gasto')}</span>
                            <span className="font-medium text-gray-900 dark:text-white">{fmtBRL(perf.cost)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('campaigns.campaignKeywords.conversions', 'Conv.')}</span>
                            <span className="font-medium text-gray-900 dark:text-white">{(Number(perf.conversions) || 0).toFixed(1)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('campaigns.campaignKeywords.costPerConv', 'Custo/Conv')}</span>
                            <span className="font-medium text-gray-900 dark:text-white">{fmtBRL(perf.costPerConversion)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('keywords.conversionRate', 'Taxa Conv.')}</span>
                            <span className="font-medium text-gray-900 dark:text-white">{fmtPct(perf.conversionRate)}</span>
                          </div>
                        </div>

                        {(perf.impressions === 0 || perf.impressions == null) && (
                          <p className="mt-3 text-xs text-gray-400 italic text-center">
                            {t('keywords.noImpressionsYet', 'Sem dados nos últimos 30 dias')}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}

          {compareKeywords.length < 2 && selectedForCompare.length < 2 && campaignKeywords.length > 0 && (
            <Card className="bg-gray-50 dark:bg-gray-800/50 border-dashed">
              <CardContent className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  {t('keywords.selectAtLeast2', 'Select at least 2 keywords above to compare')}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default CampaignKeywordsHub;
